import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {Observable, of} from 'rxjs';
import {takeWhile} from 'rxjs/operators';

import {AuthService} from '../../../../services/auth.service';
import {MultiTenantService} from '../../../../services/multi-tenant.service';
import {ConfigService} from '../../../../services/config.service';
import { UserGuide } from '../../../../models/user-guide';
import {ModalService} from '../../../../services/modal.service';
import {selectHasUserBudgets} from '../../../../store/selectors/pnp-budget.selectors';


@Component({
    selector: 'app-mobile-navigation-menu',
    templateUrl: './mobile-navigation-menu.component.html',
    styleUrls: ['./mobile-navigation-menu.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class MobileNavigationMenuComponent implements  OnInit, OnDestroy {
  @Input() visible = false;
  @Output() onBWProductsClicked = new EventEmitter<void>();
  @Output() onBWProgramsClicked = new EventEmitter<void>();
  @Output() onODProductsClicked = new EventEmitter<void>();
  @Output() onODProgramsClicked = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  buyingWindowMenuExpanded = false;
  onDemandMenuExpanded = false;
  accountMenuExpanded = false;
  supportMenuExpanded = false;
  supportSubMenuExpanded = false;
  userGuides: UserGuide[];
  budgetsVisible$: Observable<boolean> = of(false);
  private alive = true;

  constructor(private authService: AuthService,
              private router: Router,
              private configService: ConfigService,
              private multiTenantService: MultiTenantService,
              private modalService: ModalService,
              private store: Store) {
  }


  ngOnInit() {
    this.userGuides = this.configService.features.userGuides;
    this.budgetsVisible$ = this.store.select(selectHasUserBudgets).pipe(
      takeWhile(() => this.alive)
    );
  }
  ngOnDestroy() {
    this.alive = false;
  }


  bwProductsClicked() {
    this.onBWProductsClicked.emit();
    this.onClosed();
    return false;
  }

  bwProgramsClicked() {
    this.onBWProgramsClicked.emit();
    this.onClosed();
    return false;
  }

  odProductsClicked() {
    this.onODProductsClicked.emit();
    this.onClosed();
    return false;
  }

  odProgramsClicked() {
    this.onODProgramsClicked.emit();
    this.onClosed();
    return false;
  }

  onClosed(): void {
    this.closed.emit();
  }

  toggleBuyingWindowMenu(): void {
    if (this.isUserRestricted) {
      this.buyingWindowMenuExpanded = false;
      return;
    }
    this.buyingWindowMenuExpanded = !this.buyingWindowMenuExpanded;
  }

  toggleOnDemandMenu(): void {
    if (this.isUserRestricted) {
      this.onDemandMenuExpanded = false;
      return;
    }
    this.onDemandMenuExpanded = !this.onDemandMenuExpanded;
  }

  get canOrder(): boolean {
    return this.authService.canOrder && this.multiTenantService.showOnDemandLabel;
  }

  get canSeeBuyingWindows(): boolean {
    return this.authService.canBuyingWindow && this.canOrder && this.multiTenantService.showOrderWindowLabel;
  }
  get canShowTransaction(): boolean {
    return this.authService.canSeeTransactions;
  }

  get canShowPaymentPage(): boolean {
    return this.authService.canSeePaymentMethods && this.configService.showBillingInfo;
  }

  get canShowAutoshipPage(): boolean {
    return this.authService.canAutoship;
  }

  get canSeeOrders(): boolean {
    return this.authService.canSeeOrders;
  }

  get canSeeAddresses(): boolean {
    return this.authService.canSeeAddresses;
  }

  get isUserRestricted(): boolean {
    return this.authService.isRestricted;
  }

  get orderWindowLabel(): string {
    return this.multiTenantService.orderWindowLabel;
  }

  get onDemandLabel(): string {
    return this.multiTenantService.onDemandLabel;
  }

  get canSeeNotifications(): boolean {
    return this.authService.canSeeNotifications;
  }

  get canSeeDistributionGroups(): boolean {
    return this.authService.hasDistributionGroupRole;
  }

  handleOrderOnBehalfOf() {
    this.modalService.showDistributionGroupModal().subscribe( () => {
      this.closed.emit();
    });
  }

  showPnpBudgets(): void {
    this.modalService.showPnpBudgets();
    this.onClosed();
  }

}
